import { Navigation, Share2 } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";

export const fullAddress = (l) => `${l.address}, ${l.city}, ${l.state} ${l.zip}`;
const fmtMiles = (m) => (m < 10 ? `${m.toFixed(1)} mi` : `${Math.round(m)} mi`);

export const directionLinks = (loc) => {
  const dest = encodeURIComponent(fullAddress(loc));
  return {
    google: `https://www.google.com/maps/dir/?api=1&destination=${dest}`,
    apple: `https://maps.apple.com/?daddr=${dest}`,
  };
};

export async function shareText(title, text) {
  if (navigator.share) {
    try {
      await navigator.share({ title, text });
      return;
    } catch (e) {
      if (e?.name === "AbortError") return;
    }
  }
  try {
    await navigator.clipboard?.writeText(text);
    toast.success("Copied to clipboard");
  } catch {
    // clipboard unavailable
  }
  window.location.href = `sms:?&body=${encodeURIComponent(text)}`;
}

const LangShareMenu = ({ testId, onPick, children }) => (
  <DropdownMenu modal={false}>
    <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>
    <DropdownMenuContent align="end" className="bg-[#111D4A] border-white/15 text-white min-w-[170px]" onClick={(e) => e.stopPropagation()}>
      <DropdownMenuItem data-testid={`${testId}-en`} onSelect={() => onPick("en")}>Share in English</DropdownMenuItem>
      <DropdownMenuItem data-testid={`${testId}-es`} onSelect={() => onPick("es")}>Compartir en español</DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
);

const providerText = (loc, lang) => {
  const d = directionLinks(loc);
  const title = loc.company ? `${loc.company} – ${loc.name}` : loc.name;
  const note = loc.note ? `\n${loc.note}` : "";
  const [g, a] = lang === "es" ? ["Indicaciones en Google Maps", "Indicaciones en Apple Maps"] : ["Google Maps", "Apple Maps"];
  return { title, text: `${title}${note}\n${fullAddress(loc)}\n\n${g}: ${d.google}\n${a}: ${d.apple}` };
};

export const ShareProviderButton = ({ loc }) => (
  <LangShareMenu
    testId={`share-provider-${loc.id}`}
    onPick={(lang) => {
      const { title, text } = providerText(loc, lang);
      shareText(title, text);
    }}
  >
    <button
      data-testid={`share-provider-${loc.id}`}
      onClick={(e) => e.stopPropagation()}
      aria-label="Share provider"
      title="Share with client"
      className="w-7 h-7 inline-flex items-center justify-center rounded-md text-slate-300 hover:text-white bg-white/5 hover:bg-white/15 border border-white/10 transition-colors"
    >
      <Share2 className="w-3.5 h-3.5" />
    </button>
  </LangShareMenu>
);

export const DirectionsIcon = ({ loc }) => {
  const d = directionLinks(loc);
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <button
          data-testid={`directions-menu-${loc.id}`}
          onClick={(e) => e.stopPropagation()}
          aria-label="Get directions"
          className="sm:hidden w-7 h-7 inline-flex items-center justify-center rounded-md text-slate-300 hover:text-white bg-white/5 hover:bg-white/15 border border-white/10 transition-colors"
        >
          <Navigation className="w-3.5 h-3.5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-[#111D4A] border-white/15 text-white min-w-[150px]" onClick={(e) => e.stopPropagation()}>
        <DropdownMenuItem asChild>
          <a data-testid={`directions-google-${loc.id}`} href={d.google} target="_blank" rel="noopener noreferrer">Google Maps</a>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <a data-testid={`directions-apple-${loc.id}`} href={d.apple} target="_blank" rel="noopener noreferrer">Apple Maps</a>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const ShareNearestButton = ({ zipInfo, locations }) => {
  const share = (lang) => {
    const es = lang === "es";
    const top = locations.filter((l) => l._miles != null && l.note !== "Coming Soon").slice(0, 3);
    const lines = top.map(
      (l, i) => `${i + 1}. ${l.company ? `${l.company} – ${l.name}` : l.name}\n   ${fullAddress(l)} (${fmtMiles(l._miles)})`
    );
    const link = `${window.location.origin}/?zip=${zipInfo.zip}`;
    const head = es ? `Proveedores más cercanos a ${zipInfo.zip}:` : `Closest GOAT network providers to ${zipInfo.zip}:`;
    const text = `${head}\n\n${lines.join("\n")}\n\n${es ? "Ver mapa" : "View map"}: ${link}`;
    shareText(es ? "Proveedores cercanos" : "Nearest Providers", text);
  };
  return (
    <LangShareMenu testId="share-nearest" onPick={share}>
      <button
        data-testid="share-nearest-btn"
        className="ml-auto shrink-0 inline-flex items-center gap-1 text-[11px] font-bold text-[#0B132B] bg-emerald-300 hover:bg-emerald-200 rounded-full px-2.5 py-1 transition-colors"
      >
        <Share2 className="w-3 h-3" /> Share
      </button>
    </LangShareMenu>
  );
};
